import React, { useState, useEffect, useCallback } from 'react';
import api from './api';

const CATEGORIES = [
  { key: 'kyc', label: 'KYC', icon: '🪪' },
  { key: 'payroll', label: 'Payroll', icon: '💰' },
  { key: 'others', label: 'Others', icon: '📁' },
];

const DOC_TYPES = ['aadhaar', 'pan', 'passport', 'certificate', 'license', 'other'];

function getFileIcon(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return { icon: '📄', color: '#dc2626' };
  if (['doc', 'docx'].includes(ext)) return { icon: '📝', color: '#2563eb' };
  if (['xls', 'xlsx'].includes(ext)) return { icon: '📊', color: '#059669' };
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return { icon: '🖼', color: '#d97706' };
  return { icon: '📎', color: '#6366f1' };
}

export default function StaffDocumentsTab({ username }) {
  const [category, setCategory] = useState('kyc');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState('');
  const [docType, setDocType] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const BASE = process.env.REACT_APP_URL;

  const fetchFiles = useCallback(() => {
    if (!username) return;
    api.get(`${BASE}/staff-files/${username}/${category}`)
      .then(r => setFiles(r.data || []))
      .catch(() => setFiles([]));
  }, [username, category, BASE]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('document', file);
    if (description) fd.append('description', description);
    if (docType) fd.append('docType', docType);
    try {
      await api.post(`${BASE}/staff-files/${username}/${category}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDescription('');
      setDocType('');
      setShowUploadModal(false);
      fetchFiles();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleDelete(filename) {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`${BASE}/staff-files/${username}/${category}/${filename}`);
      fetchFiles();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  }

  function viewFile(serveUrl) {
    // serveUrl = "/api/staff-files/..." — strip "/api" since api instance baseURL already includes it
    var apiPath = serveUrl.startsWith('/api') ? serveUrl.slice(4) : serveUrl;
    api.get(apiPath, { responseType: 'blob' })
      .then(r => {
        var url = URL.createObjectURL(r.data);
        window.open(url, '_blank');
      })
      .catch(function() { alert('Failed to load file'); });
  }

  return (
    <div style={{ padding: 20 }}>
      {/* Header with Upload button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#2d3436' }}>
            Documents
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#636e72' }}>{username}</p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          style={{
            padding: '9px 18px', borderRadius: 20,
            background: 'linear-gradient(135deg, #6C5CE7, #a29bfe)',
            color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.82rem',
            cursor: 'pointer', boxShadow: '0 2px 8px rgba(108,92,231,0.3)',
          }}
        >
          + Upload
        </button>
      </div>

      {/* Category pills */}
      <div style={{ display: 'flex', marginBottom: 18 }}>
        {CATEGORIES.map(c => (
          <button key={c.key}
            onClick={() => setCategory(c.key)}
            style={{
              padding: '8px 16px', borderRadius: 20, border: 'none',
              marginRight: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700,
              background: category === c.key ? '#6C5CE7' : 'rgba(108,92,231,0.08)',
              color: category === c.key ? '#fff' : '#6C5CE7',
              transition: 'all 0.15s',
            }}
          >
            {c.icon} {c.label}
          </button>
        ))}
      </div>

      {/* File list */}
      {files.length === 0 ? (
        <div style={{
          padding: '48px 20px', textAlign: 'center', color: '#636e72',
          background: 'rgba(108,92,231,0.03)', borderRadius: 16, border: '1.5px dashed rgba(108,92,231,0.2)',
        }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>📂</div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#2d3436', marginBottom: 4 }}>No documents yet</div>
          <div style={{ fontSize: '0.78rem' }}>Upload documents using the button above</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {files.map(f => {
            const fi = getFileIcon(f.filename);
            return (
              <div key={f.filename} style={{
                display: 'flex', alignItems: 'center', padding: '14px 16px',
                background: '#fff', borderRadius: 14,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 3px 10px rgba(0,0,0,0.04)',
                border: '1px solid rgba(0,0,0,0.04)',
              }}>
                {/* File icon */}
                <div style={{
                  width: 42, height: 42, borderRadius: 10,
                  background: `${fi.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, marginRight: 14, flexShrink: 0,
                }}>
                  {fi.icon}
                </div>
                {/* File info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.84rem', color: '#2d3436', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.filename}
                    </span>
                    {f.docType && (
                      <span style={{
                        background: 'rgba(108,92,231,0.12)', color: '#6C5CE7',
                        padding: '2px 9px', borderRadius: 8, marginLeft: 8, fontWeight: 700, fontSize: '0.65rem',
                        textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0,
                      }}>{f.docType}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#636e72', marginTop: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    {f.description && <span>{f.description} · </span>}
                    {f.uploadedAt && new Date(f.uploadedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                    {f.encrypted && <span style={{ marginLeft: 6, color: '#00B894', fontSize: '0.7rem' }}>🔒 Encrypted</span>}
                  </div>
                </div>
                {/* Actions */}
                <button onClick={() => viewFile(f.serveUrl)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, border: 'none',
                    background: 'rgba(9,132,227,0.08)', color: '#0984e3',
                    cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, marginRight: 6,
                  }}>
                  View
                </button>
                <button onClick={() => handleDelete(f.filename)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, border: 'none',
                    background: 'rgba(225,112,85,0.08)', color: '#E17055',
                    cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
                  }}>
                  Delete
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16,
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, width: '100%', maxWidth: 420,
            padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#2d3436' }}>
                Upload Document
              </h3>
              <button onClick={() => setShowUploadModal(false)}
                style={{
                  width: 32, height: 32, borderRadius: '50%', border: 'none',
                  background: 'rgba(0,0,0,0.05)', color: '#636e72', fontSize: 16,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>✕</button>
            </div>

            {/* Category indicator */}
            <div style={{
              background: 'rgba(108,92,231,0.06)', borderRadius: 10, padding: '10px 14px',
              marginBottom: 16, fontSize: '0.8rem', color: '#6C5CE7', fontWeight: 600,
            }}>
              Uploading to: <strong>{CATEGORIES.find(c => c.key === category)?.label || category}</strong>
            </div>

            {/* Doc Type (KYC only) */}
            {category === 'kyc' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#636e72', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  Document Type
                </label>
                <select value={docType} onChange={e => setDocType(e.target.value)}
                  style={{
                    width: '100%', padding: '11px 14px', borderRadius: 10,
                    border: '1.5px solid rgba(0,0,0,0.08)', fontSize: '0.88rem',
                    background: '#fff', color: '#2d3436', outline: 'none',
                  }}>
                  <option value="">Select type...</option>
                  {DOC_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
            )}

            {/* Description */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#636e72', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Description (optional)
              </label>
              <input type="text" value={description} onChange={e => setDescription(e.target.value)}
                placeholder="e.g. Aadhaar front side"
                style={{
                  width: '100%', padding: '11px 14px', borderRadius: 10,
                  border: '1.5px solid rgba(0,0,0,0.08)', fontSize: '0.88rem',
                  background: '#fff', color: '#2d3436', outline: 'none',
                  boxSizing: 'border-box',
                }} />
            </div>

            {/* File picker area */}
            <div style={{
              border: '2px dashed rgba(108,92,231,0.3)', borderRadius: 14,
              padding: '24px 16px', textAlign: 'center', marginBottom: 16,
              background: 'rgba(108,92,231,0.03)', position: 'relative',
            }}>
              <div style={{ fontSize: 28, marginBottom: 6, opacity: 0.6 }}>📤</div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#2d3436', marginBottom: 4 }}>
                {uploading ? 'Uploading...' : 'Choose a file'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#636e72' }}>
                Images, PDF, DOC, XLS accepted
              </div>
              <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={handleUpload} disabled={uploading}
                style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  opacity: 0, cursor: 'pointer',
                }} />
            </div>

            {uploading && (
              <div style={{ textAlign: 'center', color: '#6C5CE7', fontWeight: 700, fontSize: '0.85rem' }}>
                Uploading to Drive...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
